import { Fragment, useRef, useState, useLayoutEffect, useEffect } from 'react';
import { Box, Flex, Text, HStack, VStack, Button, useBreakpointValue, Image } from '@skin-seoul/design-system';
import type { Review, ReviewImage } from '..';
import { StarIcon } from '../assets/icons/StarIcon';
import { ReviewCardImageCarousel } from './ReviewCardImageCarousel';
import { formatRelativeTime } from '@skin-seoul/service/src/utils/review/dateUtils';
import avatar from '../assets/images/avatar.png';
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
interface ReviewCardProps {
  review: Review;
  onImageClick: (image: ReviewImage, index: number) => void;
  onViewMore?: (review: Review) => void;
  isLastInColumn?: boolean;
}
export const ReviewCard = ({
  review,
  onImageClick,
  onViewMore,
  isLastInColumn: _isLastInColumn = false
}: ReviewCardProps) => {
  const isMobile = useBreakpointValue({
    base: true,
    tablet: false
  });
  const hasReviewImages = Boolean(review.images && review.images.length > 0);
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTextClamped, setIsTextClamped] = useState(false);
  useIsomorphicLayoutEffect(() => {
    const checkTextClamp = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTextClamped(element.scrollHeight > element.clientHeight);
      }
    };
    const timeoutId = setTimeout(() => {
      checkTextClamp();
    });
    window.addEventListener('resize', checkTextClamp);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTextClamp);
    };
  }, [review.content]);
  return <Flex gap="13px" w="full" data-sentry-element="Flex" data-sentry-component="ReviewCard" data-sentry-source-file="ReviewCard.tsx">
      <Box p={4} borderRadius="8px" border="1px solid #E9EAEB" w="full" flexGrow={1} h={{
      base: 'fit-content',
      tablet: '336px'
    }} data-sentry-element="Box" data-sentry-source-file="ReviewCard.tsx">
        <VStack gap={4} align="start" h="full" data-sentry-element="VStack" data-sentry-source-file="ReviewCard.tsx">
          <HStack justify="space-between" align="center" w="full" data-sentry-element="HStack" data-sentry-source-file="ReviewCard.tsx">
            <HStack gap={1} align="center" data-sentry-element="HStack" data-sentry-source-file="ReviewCard.tsx">
              <Text textStyle="heading.h5" fontWeight="semibold" color="primary.400" data-sentry-element="Text" data-sentry-source-file="ReviewCard.tsx">
                {review.rating}
              </Text>
              <StarIcon width={20} height={20} data-sentry-element="StarIcon" data-sentry-source-file="ReviewCard.tsx" />
            </HStack>

            <Text color="neutral.400" textStyle="label.small" fontWeight="normal" data-sentry-element="Text" data-sentry-source-file="ReviewCard.tsx">
              {formatRelativeTime(review.createdAt)}
            </Text>
          </HStack>
          {isMobile && hasReviewImages && <ReviewCardImageCarousel images={review.images || []} width="full" height="358px" onImageClick={onImageClick} />}
          <VStack gap={{
          base: 4,
          tablet: 3
        }} align="start" w="full" h="full" justify="space-between" data-sentry-element="VStack" data-sentry-source-file="ReviewCard.tsx">
            <VStack align="start" gap={{
            base: 4,
            tablet: 2
          }} data-sentry-element="VStack" data-sentry-source-file="ReviewCard.tsx">
              <Text textStyle="paragraph.large" fontWeight="semibold" color="neutral.800" data-sentry-element="Text" data-sentry-source-file="ReviewCard.tsx">
                {review.title}
              </Text>
              <Text ref={textRef} textStyle="paragraph.small" fontWeight="normal" color="neutral.500" maxH={{
              base: 'auto'
            }} lineClamp={3} data-sentry-element="Text" data-sentry-source-file="ReviewCard.tsx">
                {review.content}
              </Text>
              {isTextClamped && <div>
                  <Button variant="plain" textStyle="label.small" color="primary.400" maxH="fit-content" maxW="fit-content" px={0} onClick={() => onViewMore?.(review)}>
                    View more
                  </Button>
                </div>}
            </VStack>
            <HStack borderRadius="8px" p={2} bg="neutral.50" w="full" gap={2} data-sentry-element="HStack" data-sentry-source-file="ReviewCard.tsx">
              <Image src={avatar.src} alt="avatar" w={8} h={8} borderRadius="50%" flexShrink={0} data-sentry-element="Image" data-sentry-source-file="ReviewCard.tsx" />
              <VStack align="start" gap={0.5} w="full" data-sentry-element="VStack" data-sentry-source-file="ReviewCard.tsx">
                <Text textStyle="label.small" fontWeight="medium" color="neutral.800" data-sentry-element="Text" data-sentry-source-file="ReviewCard.tsx">
                  {review.author.name}
                </Text>
                <HStack gap={1} flexWrap="wrap" data-sentry-element="HStack" data-sentry-source-file="ReviewCard.tsx">
                  {[...review.skinType, ...review.skinConcern].map((type, index, array) => <Fragment key={index}>
                      <Text textStyle="label.xsmall" fontWeight="normal" color="neutral.700">
                        {type}
                      </Text>
                      {index < array.length - 1 && <Box w={0.5} h={0.5} bg="neutral.100" borderRadius="50%" />}
                    </Fragment>)}
                </HStack>
              </VStack>
            </HStack>
          </VStack>
        </VStack>
      </Box>
      {!isMobile ? hasReviewImages ? <ReviewCardImageCarousel images={review.images || []} width="279px" height="336px" onImageClick={onImageClick} /> : <Box w="279px" flexShrink={0} /> : null}
    </Flex>;
};
export default ReviewCard;